import { useEffect } from "react";
import { 
    m,
    getOperationalContext,
    getNextOperationalChange 
} from '../../utils/dateUtils';
import { useAuthStore } from "../../stores/useAuthStore";
import { useDateStore } from "../../stores/useDateStore";
import { useOperationalMapStore } from "../../stores/useOperationalMapStore";
import { useHourFilterStore } from "../../stores/useHourFilterStore";
import { useScreenStore, isSocketScreen } from "../../stores/useScreenStore";

const TEST_MODE = false;
const TEST_INTERVAL_MS = 5 * 1000;

const TEST_DATES = [
    '2026-08-14 19:59:00',
    '2026-08-14 20:00:00',
    '2026-08-14 23:59:00',
    '2026-08-15 00:00:00',
    '2026-08-15 07:59:00',
    '2026-08-15 08:00:00'
];

export default function useOperationalRangeWatcher() {

    const user = useAuthStore(state => state.user);
    
    const screen = useScreenStore(state => state.screen);
    
    const monitorMode = useScreenStore(state => state.monitorMode);
    const autoOperationalRange = useDateStore(state => state.autoOperationalRange);
    const operationalContextInitialized =
        useHourFilterStore(state => state.operationalContextInitialized);

    const isOperationalMapMode = useOperationalMapStore(
        state => state.isOperationalMapMode
    );

    useEffect(() => {

        if(!isSocketScreen(screen)) return;
        
        if(user === null) return;

        if (isOperationalMapMode) {

            console.log(
                '⛔ Operational Range Watcher desabilitado: Mapa Operacional'
            );

            return;
        }

        const needsInitialSync =
            monitorMode === 2 &&
            screen === 'Filtros';

        if (
            needsInitialSync &&
            !operationalContextInitialized
        ) {

            console.log(
                '⏳ Aguardando sincronização inicial do contexto operacional'
            );

            return;
        }

        const useOperationalRange = user
                ?.capabilities
                ?.hourRangePerm
                ?.useOperationalRange;

        const shouldRunWatcher =
            useOperationalRange &&
            (
                monitorMode !== 2 ||
                screen === 'Filtros'
            );

        if (!shouldRunWatcher) {

            console.log('⛔ Operational Range Watcher desabilitado');

            return;

        }

        function checkOperationalRange(now = m()) {

            console.group('🕐 Operational Range Watcher');

            console.log('Agora:', now.format('YYYY-MM-DD HH:mm:ss'));

            const { autoOperationalRange } = useDateStore.getState();

            if (!autoOperationalRange) {

                console.log('⛔ Usuário alterou data/range manualmente');

                console.groupEnd();

                return;
            }

            const { date, hourRange } = getOperationalContext(now);

            const dateStore = useDateStore.getState();

            const hourStore = useHourFilterStore.getState();

            const operationalDate = date.format('YYYY-MM-DD');

            console.log('Data atual:', dateStore.today);

            console.log('Data operacional esperada:', operationalDate);

            console.log('Range atual:', hourStore.hourRange);

            console.log('Range esperado:', hourRange);

            // =========================
            // DATA
            // =========================
            if (dateStore.today !== operationalDate) {

                console.log(
                    '📅 ALTERANDO DATA:',
                    dateStore.today,
                    '→',
                    operationalDate
                );

                dateStore.setSelectedDate(date, true, false);

            } else {

                console.log('✅ Data já está correta');

            }

            // =========================
            // RANGE
            // =========================
            const currentRange = hourStore.hourRange;

            if (
                currentRange[0] !== hourRange[0] ||
                currentRange[1] !== hourRange[1]
            ) {

                console.log(
                    '⏰ ALTERANDO RANGE:',
                    currentRange,
                    '→',
                    hourRange
                );

                hourStore.setHourRange(hourRange, true, false);

            } else {

                console.log( '✅ Range já está correto');

            }

            console.groupEnd();

        }

        // =========================
        // TESTE
        // =========================

        if (TEST_MODE) {

            let index = 0;

            checkOperationalRange(
                m(TEST_DATES[index])
            );

            const interval = setInterval(() => {

                index++;

                if (index >= TEST_DATES.length) {
                    clearInterval(interval);
                    return;
                }

                checkOperationalRange(
                    m(TEST_DATES[index])
                );

            }, TEST_INTERVAL_MS);

            return () => {
                clearInterval(interval);
            };

        }

        // =========================
        // PRODUÇÃO
        // =========================

        checkOperationalRange();

        let timeout;

        function scheduleNextChange() {
            
            const now = m();

            const nextChange = getNextOperationalChange(now);

            const delay = nextChange.diff(now);

            timeout = setTimeout(() => {

                checkOperationalRange();

                // agenda a próxima virada
                scheduleNextChange();

            }, delay);

        }

        function handleVisibilityChange() {
            
            if (document.visibilityState !== 'visible') {
                return;
            }

            // Primeiro corrige o estado
            // caso alguma virada tenha ocorrido
            // enquanto a aba estava suspensa.
            checkOperationalRange();

            // Cancela o timeout antigo, pois ele
            // pode estar defasado.
            clearTimeout(timeout);

            // Recalcula a próxima virada a partir
            // da hora real atual.
            scheduleNextChange();
        }

        document.addEventListener('visibilitychange', handleVisibilityChange);

        scheduleNextChange();

        return () => {

            clearTimeout(timeout);

            document.removeEventListener('visibilitychange', handleVisibilityChange);

        };

    }, [
        user,
        monitorMode,
        screen,
        autoOperationalRange,
        operationalContextInitialized,
        isOperationalMapMode
    ]);

}